"use client";

import { useState } from 'react';
import ToolLayout from '@/components/layout/ToolLayout';
import SEOArticle from '@/components/layout/SEOArticle';
import { Mail, ShieldCheck, Cpu, Clock, Globe } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export default function Contact() {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: 'General Inquiry',
        message: ''
    });
    const [status, setStatus] = useState({
        loading: false,
        success: false,
        error: ''
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const validateEmail = (email: string) => {
        return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
            setStatus({ loading: false, success: false, error: 'Please fill in all required fields.' });
            return;
        }

        if (!validateEmail(formData.email)) {
            setStatus({ loading: false, success: false, error: 'Please enter a valid email address.' });
            return;
        }

        setStatus({ loading: true, success: false, error: '' });

        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setStatus({ loading: false, success: true, error: '' });
                setFormData({
                    name: '',
                    email: '',
                    subject: 'General Inquiry',
                    message: ''
                });
            } else {
                const data = await res.json();
                setStatus({ loading: false, success: false, error: data.error || t('contact_error_prefix') });
            }
        } catch (err) {
            setStatus({ loading: false, success: false, error: 'Connection failed. Please check your internet.' });
        }
    };

    const longContent = (
        <SEOArticle
            title={t('contact_title')}
            sections={[
                {
                    heading: t('contact_copyright_title'),
                    content: (
                        <>
                            <p>{t('contact_copyright_desc')}</p>
                            <p>Please include the following for faster processing:</p>
                            <ul>
                                <li>Your name and contact details</li>
                                <li>Description of the content in question</li>
                                <li>Proof of ownership or authority</li>
                                <li>Clear request (removal or modification)</li>
                            </ul>
                            <p>We review such requests carefully and respond in accordance with applicable standards and industry practices.</p>
                        </>
                    )
                },
                {
                    heading: t('contact_tech_support_title'),
                    content: (
                        <>
                            <p>{t('contact_tech_support_desc')}</p>
                            <ul>
                                <li>{t('contact_tech_support_item1')}</li>
                                <li>{t('contact_tech_support_item2')}</li>
                                <li>{t('contact_tech_support_item3')}</li>
                            </ul>
                        </>
                    )
                },
                {
                    heading: "III. Institutional Governance: Our Communication Protocols",
                    content: (
                        <>
                            <p>We mandate a rigorous "Hardened Ingress Protocol" for all inbound telemetry and inquiries. Every data transmission is subjected to multi-tier payload scrubbing to neutralize Cross-Site Scripting (XSS) and recursive injection vectors, ensuring our support infrastructure mirrors the isolation standards of our core utility nodes.</p>
                            <p>Our support philosophy is built on the principle of <strong>Forensic Clarity</strong>. We require detailed, objective breakdowns of technical deviations to allow our engineering lead to isolate edge cases without redundant follow-up cycles. In mission-critical operations, resolution latency is the terminal metric.</p>
                        </>
                    )
                },
                {
                    heading: "IV. Operational Data Sovereignty: Secure Support Protocols",
                    content: (
                        <>
                            <p>Standard technical support often introduces significant data-leakage vectors during the diagnostic phase. CorpToolset mitigates this risk via a mandatory <strong>Zero-Data-Attachment Policy</strong>. We strictly prohibit the transmission of proprietary datasets, cryptographic keys, or PII through support channels. Instead, we provide the technical primitives required to execute local-first debugging within your isolated browser sandbox.</p>
                            <p>This protocol guarantees absolute data confidentiality throughout the diagnostic phase. By architecting the logic for local-first environment auditing, we uphold our Zero-Knowledge mandate while accelerating your professional output.</p>
                        </>
                    )
                },
                {
                    heading: "V. Procedural Lifecycle: Support Ticket Orchestration",
                    content: (
                        <>
                            <p>Every inquiry at CorpToolset follows a structured industrial lifecycle designed to ensure comprehensive resolution. Upon submission, your query is categorized by our internal logic engine based on its technical domain Security, Financial, Developer, or Content. This ensures it is routed to a specialist with the precise domain expertise required to provide a high-authority response.</p>
                            <p>We bypass automated placeholder cycles to deliver substantive, technical feedback targeting the root architectural cause of each inquiry. This prioritized focus on resolution density over ticket volume has established our support node as the definitive resource for a global network of 500,000+ professionals.</p>
                        </>
                    )
                }
            ]}
        />
    );

    return (
        <ToolLayout
            title={t('contact_title')}
            description={t('contact_desc')}
            hideSidebar={true}
            longContent={longContent}
            jsonLdType="WebPage"
            path="contact"
        >
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '2.5rem',
                    marginBottom: '2.5rem'
                }}>
                    <div className="card" style={{ padding: '2.5rem', border: '1px solid #e2e8f0', borderRadius: '24px' }}>
                        <div style={{ marginBottom: '1.5rem', color: 'var(--primary)' }}><Mail size={32} /></div>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '1rem' }}>{t('nav_contact')}</h3>
                        <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                            You can contact us directly via email for specific departments:
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div style={{ fontSize: '0.9rem' }}>
                                <strong style={{ color: '#0f172a' }}>General Support:</strong><br />
                                <a href="mailto:support@corptoolset.com" style={{ color: 'var(--primary)', textDecoration: 'none' }} className="hover-underline">support@corptoolset.com</a>
                            </div>
                            <div style={{ fontSize: '0.9rem' }}>
                                <strong style={{ color: '#0f172a' }}>Business & Partnerships:</strong><br />
                                <a href="mailto:business@corptoolset.com" style={{ color: 'var(--primary)', textDecoration: 'none' }} className="hover-underline">business@corptoolset.com</a>
                            </div>
                            <div style={{ fontSize: '0.9rem' }}>
                                <strong style={{ color: '#0f172a' }}>Copyright & Legal:</strong><br />
                                <a href="mailto:legal@corptoolset.com" style={{ color: 'var(--primary)', textDecoration: 'none' }} className="hover-underline">legal@corptoolset.com</a>
                            </div>
                        </div>
                    </div>

                    <div className="card" style={{ padding: '2.5rem', border: '1px solid #e2e8f0', borderRadius: '24px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                            <div>
                                <div style={{ color: '#3b82f6', marginBottom: '0.75rem' }}><Clock size={24} /></div>
                                <h4 style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '0.5rem' }}>{t('contact_response_time_title')}</h4>
                                <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>{t('contact_response_time_desc')}</p>
                            </div>
                            <div>
                                <div style={{ color: '#10b981', marginBottom: '0.75rem' }}><Globe size={24} /></div>
                                <h4 style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '0.5rem' }}>{t('contact_availability_title')}</h4>
                                <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>{t('contact_availability_desc')}</p>
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                                <div style={{ color: '#ef4444', marginBottom: '0.75rem' }}><ShieldCheck size={24} /></div>
                                <h4 style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '0.5rem' }}>{t('contact_legal_privacy_title')}</h4>
                                <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>{t('contact_legal_privacy_desc')}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card" style={{ padding: 'clamp(1.5rem, 5vw, 2.5rem)', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.08)', borderRadius: '32px', border: '1px solid #f1f5f9' }}>
                    <h2 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '2rem', textAlign: 'center' }}>{t('contact_form_title')}</h2>

                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                        {status.success && (
                            <div style={{ padding: '1.5rem', background: '#dcfce7', color: '#166534', borderRadius: '12px', border: '1px solid #bbf7d0', textAlign: 'center', fontWeight: 600 }}>
                                ✅ {t('contact_success_msg')}
                            </div>
                        )}

                        {status.error && (
                            <div style={{ padding: '1.5rem', background: '#fee2e2', color: '#991b1b', borderRadius: '12px', border: '1px solid #fecaca', textAlign: 'center', fontWeight: 600 }}>
                                ❌ {status.error}
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
                            <div className="form-group">
                                <label htmlFor="name" style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 700, color: '#1e293b' }}>{t('contact_name_label')}</label>
                                <input
                                    id="name"
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder={t('contact_name_placeholder') || "e.g. John Doe"}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '1rem',
                                        borderRadius: '12px',
                                        border: '2px solid #f1f5f9',
                                        background: '#f8fafc',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email" style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 700, color: '#1e293b' }}>{t('contact_email_label')}</label>
                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder={t('contact_email_placeholder') || "e.g. john@example.com"}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '1rem',
                                        borderRadius: '12px',
                                        border: '2px solid #f1f5f9',
                                        background: '#f8fafc',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="subject" style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 700, color: '#1e293b' }}>{t('contact_subject_label')}</label>
                            <select
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                style={{
                                    width: '100%',
                                    padding: '1rem',
                                    borderRadius: '12px',
                                    border: '2px solid #f1f5f9',
                                    background: '#f8fafc',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value="General Inquiry">{t('contact_subject_general') || "General Inquiry"}</option>
                                <option value="Business / Partnership">{t('contact_subject_business') || "Business / Partnership"}</option>
                                <option value="Copyright / Content Issue">{t('contact_subject_legal') || "Copyright / Content Issue"}</option>
                                <option value="Technical Issue">{t('contact_subject_tech') || "Technical Issue"}</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="message" style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 700, color: '#1e293b' }}>{t('contact_message_label')}</label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder={t('contact_message_placeholder') || "Describe the specific issue, unexpected output, or tool request here..."}
                                required
                                rows={6}
                                style={{
                                    width: '100%',
                                    padding: '1rem',
                                    borderRadius: '12px',
                                    border: '2px solid #f1f5f9',
                                    background: '#f8fafc',
                                    fontSize: '1rem',
                                    resize: 'vertical'
                                }}
                            ></textarea>
                        </div>

                        <button
                            type="submit"
                            disabled={status.loading}
                            style={{
                                background: 'var(--primary)',
                                color: '#fff',
                                padding: '1.25rem',
                                borderRadius: '16px',
                                fontWeight: 800,
                                fontSize: '1.1rem',
                                border: 'none',
                                cursor: status.loading ? 'not-allowed' : 'pointer',
                                opacity: status.loading ? 0.7 : 1,
                                transition: 'transform 0.2s ease'
                            }}
                            onMouseOver={(e) => !status.loading && (e.currentTarget.style.transform = 'scale(1.02)')}
                            onMouseOut={(e) => !status.loading && (e.currentTarget.style.transform = 'scale(1)')}
                        >
                            {status.loading ? t('contact_submitting_btn') : t('contact_submit_btn')}
                        </button>
                    </form>
                </div>

                <div style={{ marginTop: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.9rem' }}>
                    Thank you for contacting CorpToolset.
                </div>
            </div>
        </ToolLayout>
    );
}


